import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import styles from "./NightForm.module.css";

function NightForm() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Выбор причины отгула временно отключён. Для возврата двух вариантов
  // раскомментировать состояние, эффект, переключатели и старую отправку ниже.
  // const [toggle, setToggle] = useState("night");
  const [date, setDate] = useState("");
  // const [comment, setComment] = useState("");
  const [comment, setComment] = useState("С ночи");

  const [loading, setLoading] = useState(false);

  // success modal only
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("Отгул успешно отправлен");

  // inline error alert above submit
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const closeSuccess = () => setSuccessOpen(false);
  const clearError = () => {
    setErrorTitle("");
    setErrorMessage("");
  };

  // защита
  useEffect(() => {
    if (!currentUser) navigate("/");
  }, [currentUser, navigate]);

  // дата по умолчанию
  useEffect(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    setDate(`${yyyy}-${mm}-${dd}`);
  }, []);

  // Старый эффект для переключения между причинами отгула.
  /*
  useEffect(() => {
    if (toggle === "night" && (!comment || comment.trim() === "")) {
      setComment("С ночи");
    }
    // eslint-disable-next-line
  }, [toggle]);
  */

  // редирект через 3 секунды после успеха
  useEffect(() => {
    if (!successOpen) return;
    const timer = setTimeout(() => navigate("/dashboard"), 3000);
    return () => clearTimeout(timer);
  }, [successOpen, navigate]);

  const canSubmit = useMemo(() => date.trim() !== "" && !loading, [date, loading]);

  async function safeReadResponse(response) {
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      try {
        return await response.json();
      } catch {
        return {};
      }
    }
    try {
      const text = await response.text();
      return { details: text };
    } catch {
      return {};
    }
  }

  function normalizeErrorMessage(status, data) {
    const raw =
      data?.details ||
      data?.error ||
      data?.message ||
      (typeof data === "string" ? data : "") ||
      "";

    if (status === 403) return raw || "Недостаточно прав";
    if (status === 401) return raw || "Требуется авторизация";
    if (status >= 500) return raw || "Ошибка сервера";
    return raw || "Ошибка при отправке";
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    // перед новой отправкой чистим сообщения
    clearError();
    setSuccessOpen(false);

    setLoading(true);

    try {
      const response = await fetch("/api/night/event-from-night-work", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        // Старый вариант с выбором причины:
        // body: JSON.stringify({ toggle, date, comment }),
        body: JSON.stringify({ toggle: "night", date, comment }),
      });

      const data = await safeReadResponse(response);

      if (response.ok) {
        const msg = data?.details || data?.message || "Отгул успешно отправлен";
        setSuccessMessage(String(msg));
        setSuccessOpen(true);
      } else {
        const msg = normalizeErrorMessage(response.status, data);
        setErrorTitle("Не удалось отправить");
        setErrorMessage(String(msg));
      }
    } catch (err) {
      setErrorTitle("Ошибка сети");
      setErrorMessage("Не удалось связаться с сервером. " + (err?.message || ""));
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) return <div>Загрузка...</div>;

  return (
    <div className={styles.container}>
      {successOpen && (
        <div
          className={styles.successOverlay}
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeSuccess();
          }}
        >
          <div className={styles.successModal}>
            <div className={styles.successIcon} aria-hidden="true" />
            <h3 className={styles.successMessage}>Успешно!</h3>
            <p className={styles.redirectText}>{successMessage}</p>

            <div className={styles.progressBar}>
              <div className={styles.progressFill}></div>
            </div>

            <p className={styles.redirectText}>Переход в кабинет через 3 секунды...</p>
          </div>
        </div>
      )}

      <div className={styles.formContainer}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.topControls}>
            <h2 className={styles.formTitle}>Отгул с ночной смены</h2>

            {/* Старый выбор причины отгула — временно отключён.
                Чтобы вернуть, раскомментировать блок и убрать заголовок выше.
            <div className={styles.toggle}>
              <input
                type="radio"
                id="night"
                checked={toggle === "night"}
                onChange={() => setToggle("night")}
                className={styles.toggleInput}
              />
              <label
                htmlFor="night"
                className={`${styles.toggleLabel} ${
                  toggle === "night" ? styles.active : ""
                }`}
              >
                С ночной смены
              </label>

              <input
                type="radio"
                id="sick"
                checked={toggle === "sick"}
                onChange={() => setToggle("sick")}
                className={styles.toggleInput}
              />
              <label
                htmlFor="sick"
                className={`${styles.toggleLabel} ${
                  toggle === "sick" ? styles.active : ""
                }`}
              >
                Плохое самочувствие
              </label>
            </div>
            */}

            <button
              className={styles.backButton}
              type="button"
              onClick={() => navigate("/dashboard")}
            >
              Назад
            </button>
          </div>

          <div className={styles.inputField}>
            <label className={styles.label}>Дата отгула *</label>
            <input
              type="date"
              className={styles.input}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          <div className={styles.inputField}>
            <label className={styles.label}>Комментарий (не обязательно)</label>
            <textarea
              className={`${styles.input} ${styles.comment}`}
              rows="3"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>

          {/* Error alert прямо над кнопкой */}
          {(errorTitle || errorMessage) && (
            <div className={`${styles.alert} ${styles.alertError}`}>
              <div>
                <p className={styles.alertText}>{errorTitle || "Ошибка"}</p>
                {errorMessage && <p className={styles.alertSubText}>{errorMessage}</p>}
              </div>
              <button type="button" className={styles.alertButton} onClick={clearError}>
                Понятно
              </button>
            </div>
          )}

          <button type="submit" className={styles.sendButton} disabled={!canSubmit}>
            {loading ? "Отправка..." : "Отправить"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default NightForm;